/**
 * API Service Layer
 * Centralized API calls with error handling and request cancellation
 */
import axios from "axios";
import { API_ENDPOINTS } from "../config/api.config";

// Create axios instance with default config
const apiClient = axios.create({
  timeout: 30000, // 30 seconds
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      if (status === 401) {
        // Handle unauthorized
      } else if (status === 404) {
        // Handle not found
      } else if (status >= 500) {
        // Handle server errors
      }
    } else if (error.request) {
      // Request made but no response received
    } else {
      // Error setting up request
    }
    return Promise.reject(error);
  }
);

/**
 * Fetch LinkedIn posts
 * @returns {Promise} Axios response
 */
export const fetchLinkedInPosts = async () => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.LINKEDIN_POSTS);
    
    // Handle new API response format: { success: true, count: X, data: [...] }
    if (response.data && response.data.success && response.data.data) {
      return { data: response.data.data, success: true };
    } else if (response.data && Array.isArray(response.data)) {
      // Fallback for old format (direct array)
      return { data: response.data, success: true };
    } else {
      return { data: [], success: true };
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Submit contact form
 * @param {Object} formData - Contact form data
 * @returns {Promise} Axios response
 */
export const submitContactForm = async (formData) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.CONTACT_US, formData);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Submit talent application
 * @param {FormData} formData - Application form data with attachments
 * @returns {Promise} Axios response
 */
export const submitTalentApplication = async (formData) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.APPLY, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export default apiClient;
